import { createFileRoute } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, ShieldCheck } from "lucide-react";

export const Route = createFileRoute("/admin/sub-admins")({
  head: () => ({ meta: [{ title: "Sub Admins — Admin" }] }),
  component: SubAdminsPage,
});

function SubAdminsPage() {
  const qc = useQueryClient();
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  const { data: subs, isLoading } = useQuery({
    queryKey: ["sub_admins"],
    queryFn: async () => {
      const { data: roles, error } = await supabase
        .from("user_roles").select("user_id, created_at").eq("role", "sub_admin");
      if (error) throw error;
      if (!roles?.length) return [];
      const ids = roles.map((r) => r.user_id);
      const { data: profs } = await supabase.from("profiles").select("id, name, phone").in("id", ids);
      return roles.map((r) => ({
        ...r,
        profile: profs?.find((p) => p.id === r.user_id),
      }));
    },
  });

  const { data: logs } = useQuery({
    queryKey: ["admin_action_logs"],
    queryFn: async () => {
      const { data } = await (supabase as any).from("admin_action_logs")
        .select("*").order("created_at", { ascending: false }).limit(100);
      return data ?? [];
    },
  });

  async function add() {
    if (!phone.trim()) return toast.error("Enter mobile number");
    setBusy(true);
    const { error } = await (supabase as any).rpc("admin_add_sub_admin", { _phone: phone.trim() });
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success("Sub-admin added");
    setPhone("");
    qc.invalidateQueries();
  }

  async function remove(userId: string) {
    if (!confirm("Remove sub-admin?")) return;
    const { error } = await (supabase as any).rpc("admin_remove_sub_admin", { _user_id: userId });
    if (error) return toast.error(error.message);
    toast.success("Sub-admin removed");
    qc.invalidateQueries();
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-black">Sub Admins</h1>
      <p className="text-xs text-muted-foreground">Sub Admins can only approve/reject Deposits and declare Results.</p>

      <div className="rounded-2xl border bg-card p-4">
        <p className="mb-2 text-sm font-bold">Add Sub Admin by mobile</p>
        <div className="flex gap-2">
          <Input placeholder="+91XXXXXXXXXX or 10 digits" value={phone} onChange={(e) => setPhone(e.target.value)} />
          <Button onClick={add} disabled={busy}>Add</Button>
        </div>
      </div>

      <div className="rounded-2xl border bg-card p-4">
        <p className="mb-3 text-sm font-bold">Current Sub Admins</p>
        {isLoading && <p className="text-xs text-muted-foreground">Loading…</p>}
        {!isLoading && (subs ?? []).length === 0 && (
          <p className="text-xs text-muted-foreground">No sub-admins yet.</p>
        )}
        <div className="divide-y">
          {(subs ?? []).map((s: any) => (
            <div key={s.user_id} className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <ShieldCheck className="h-4 w-4 text-primary" />
                <div>
                  <p className="text-sm font-semibold">{s.profile?.name ?? "—"}</p>
                  <p className="text-[11px] text-muted-foreground">{s.profile?.phone ?? ""}</p>
                </div>
              </div>
              <Button variant="destructive" size="sm" onClick={() => remove(s.user_id)}>
                <Trash2 className="h-3.5 w-3.5" /> Remove
              </Button>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border bg-card p-4">
        <p className="mb-3 text-sm font-bold">Admin Action Log</p>
        <div className="max-h-80 overflow-y-auto space-y-1 text-xs">
          {(logs ?? []).map((l: any) => (
            <div key={l.id} className="flex justify-between gap-2 border-b py-1">
              <div>
                <span className="font-semibold">{l.admin_name ?? l.admin_phone ?? "admin"}</span>{" "}
                <span className="text-muted-foreground">→ {l.action}</span>{" "}
                {l.target_phone && <span className="text-muted-foreground">({l.target_phone})</span>}
              </div>
              <span className="text-muted-foreground">{new Date(l.created_at).toLocaleString("en-IN")}</span>
            </div>
          ))}
          {(!logs || logs.length === 0) && <p className="text-muted-foreground">No actions recorded yet.</p>}
        </div>
      </div>
    </div>
  );
}
